import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { StatusPill } from '@/components/ui/StatusPill'
import type {
  AdminDashboardData,
  DashboardEmployerRow,
  DashboardFunnelStage,
  DashboardProgramRow,
  DashboardRecentHire,
  DashboardSkillRow,
} from '@/features/admin/api/dashboard.api'
import { formatRelativeTime } from '@/utils/format'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const AVATAR_TONES = ['primary', 'secondary', 'success', 'info', 'warning', 'danger'] as const

function toneFor(index: number) {
  return AVATAR_TONES[index % AVATAR_TONES.length] ?? 'primary'
}

function initials(name: string | null) {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? '?'
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + second).toUpperCase()
}

function percent(part: number, whole: number) {
  return whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0
}

export function InitialsAvatar({ name, index }: { name: string | null; index: number }) {
  const tone = toneFor(index)
  return (
    <span className={`avatar avatar-sm avatar-rounded bg-${tone}-transparent text-${tone} fw-semibold fs-12 flex-shrink-0`}>
      {initials(name)}
    </span>
  )
}

export function SectionCard({
  title,
  subtitle,
  action,
  children,
  bodyClass = 'card-body',
}: {
  title: string
  subtitle?: string
  action?: ReactNode
  children: ReactNode
  bodyClass?: string
}) {
  return (
    <div className="card custom-card h-100">
      <div className="card-header border-bottom d-flex align-items-center justify-content-between gap-2">
        <div>
          <p className="fw-semibold fs-15 mb-0">{title}</p>
          {subtitle && <p className="fs-12 text-muted mb-0">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className={bodyClass}>{children}</div>
    </div>
  )
}

export function EmptyState({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="text-center text-muted py-4">
      <i className={`ti ${icon} fs-28 d-block mb-2 op-5`} />
      <span className="fs-13">{text}</span>
    </div>
  )
}

// ─── KPI tiles ───────────────────────────────────────────────────────────────

export function ProgressTile({
  label,
  value,
  caption,
  progress,
  tone,
  iconClass,
}: {
  label: string
  value: string
  caption: string
  progress: number
  tone: string
  iconClass: string
}) {
  const pct = Math.max(0, Math.min(100, progress))
  return (
    <div className="col-xl-4 col-md-6">
      <div className="card custom-card h-100">
        <div className="card-body">
          <div className="d-flex align-items-start justify-content-between mb-3">
            <div>
              <p className="fs-13 text-muted mb-1">{label}</p>
              <p className="fs-28 fw-bold mb-0 lh-1">{value}</p>
            </div>
            <div
              className={`rounded-3 d-flex align-items-center justify-content-center bg-${tone}-transparent text-${tone}`}
              style={{ width: 52, height: 52, flexShrink: 0 }}
            >
              <i className={`ti ${iconClass} fs-22`} />
            </div>
          </div>
          <div
            className="progress progress-xs mb-2"
            role="progressbar"
            aria-label={label}
            aria-valuenow={Math.round(pct)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className={`progress-bar bg-${tone}`} style={{ width: `${pct}%` }} />
          </div>
          <p className="fs-12 text-muted mb-0">{caption}</p>
        </div>
      </div>
    </div>
  )
}

export function InsightTiles({ data }: { data: AdminDashboardData }) {
  const { placement, activeEmployers, openPositions } = data
  return (
    <div className="row g-3 mb-4">
      <ProgressTile
        label="Placement Rate"
        value={`${placement.ratePercent}%`}
        caption={`${placement.hired.toLocaleString()} of ${placement.applied.toLocaleString()} students who applied were hired`}
        progress={placement.ratePercent}
        tone="success"
        iconClass="ti-target-arrow"
      />
      <ProgressTile
        label="Active Employers"
        value={activeEmployers.active.toLocaleString()}
        caption={`of ${activeEmployers.approved.toLocaleString()} approved employers have a live job`}
        progress={percent(activeEmployers.active, activeEmployers.approved)}
        tone="primary"
        iconClass="ti-building-skyscraper"
      />
      <ProgressTile
        label="Open Positions"
        value={openPositions.remaining.toLocaleString()}
        caption={`${openPositions.filled.toLocaleString()} of ${openPositions.openings.toLocaleString()} openings on live jobs filled`}
        progress={percent(openPositions.filled, openPositions.openings)}
        tone="warning"
        iconClass="ti-armchair"
      />
    </div>
  )
}

// ─── Hiring funnel ───────────────────────────────────────────────────────────

const FUNNEL_LABEL: Record<DashboardFunnelStage['stage'], string> = {
  applied: 'Applied',
  under_review: 'Under review',
  shortlisted: 'Shortlisted',
  hired: 'Hired',
}

// One hue, light → dark, so the bar darkens as candidates get closer to hire.
const FUNNEL_OPACITY = [0.35, 0.55, 0.78, 1]

export function HiringFunnel({
  stages,
  subtitle = 'Applications that reached each stage',
}: {
  stages: DashboardFunnelStage[]
  subtitle?: string
}) {
  const top = stages[0]?.count ?? 0

  return (
    <SectionCard title="Hiring Funnel" subtitle={subtitle}>
      {top === 0 ? (
        <EmptyState icon="ti-filter" text="No applications yet." />
      ) : (
        <div className="d-flex flex-column gap-3">
          {stages.map((stage, i) => {
            const prev = i > 0 ? (stages[i - 1]?.count ?? 0) : 0
            const ofTop = percent(stage.count, top)
            return (
              <div key={stage.stage} title={`${FUNNEL_LABEL[stage.stage]}: ${stage.count.toLocaleString()} (${ofTop}% of applications)`}>
                <div className="d-flex align-items-baseline justify-content-between mb-1">
                  <span className="fs-13 fw-medium">{FUNNEL_LABEL[stage.stage]}</span>
                  <span className="fs-13">
                    <strong>{stage.count.toLocaleString()}</strong>
                    <span className="text-muted fs-12 ms-2">{ofTop}%</span>
                  </span>
                </div>
                <div className="d-flex justify-content-center">
                  <div
                    className="bg-primary rounded-1"
                    style={{
                      width: `${Math.max(ofTop, 2)}%`,
                      height: 26,
                      opacity: FUNNEL_OPACITY[i] ?? 1,
                      transition: 'width .3s',
                    }}
                  />
                </div>
                {i > 0 && (
                  <p className="fs-11 text-muted mb-0 mt-1 text-center">
                    {percent(stage.count, prev)}% of {FUNNEL_LABEL[stages[i - 1]?.stage ?? 'applied'].toLowerCase()}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      )}
    </SectionCard>
  )
}

// ─── Skills: demand vs supply ────────────────────────────────────────────────

// Two measures with very different scales (jobs vs students), so each side
// of the butterfly has its own scale and every bar carries its value.
export function SkillsDemandSupply({ rows }: { rows: DashboardSkillRow[] }) {
  const maxDemand = Math.max(...rows.map((r) => r.demand), 1)
  const maxSupply = Math.max(...rows.map((r) => r.supply), 1)

  return (
    <SectionCard title="Skills in Demand vs Supply" subtitle="Top skills on live jobs, and how many students have them">
      {rows.length === 0 ? (
        <EmptyState icon="ti-chart-bar" text="No live jobs list required skills yet." />
      ) : (
        <>
          <div className="d-flex align-items-center fs-12 text-muted mb-3">
            <span className="flex-fill text-end d-flex align-items-center justify-content-end gap-1">
              Jobs asking for it
              <span className="bg-primary rounded-1 d-inline-block" style={{ width: 10, height: 10 }} />
            </span>
            <span style={{ width: 130 }} />
            <span className="flex-fill d-flex align-items-center gap-1">
              <span className="bg-secondary rounded-1 d-inline-block" style={{ width: 10, height: 10 }} />
              Students who have it
            </span>
          </div>
          <div className="d-flex flex-column gap-2">
            {rows.map((row) => (
              <div
                key={row.skill}
                className="d-flex align-items-center"
                title={`${row.skill}: ${row.demand} live job${row.demand === 1 ? '' : 's'}, ${row.supply} student${row.supply === 1 ? '' : 's'}`}
              >
                <div className="flex-fill d-flex align-items-center justify-content-end gap-2" style={{ minWidth: 0 }}>
                  <span className="fs-12 fw-semibold">{row.demand}</span>
                  <div
                    className="bg-primary rounded-start-1"
                    style={{ width: `${(row.demand / maxDemand) * 80}%`, height: 16, minWidth: 3 }}
                  />
                </div>
                <span className="fs-12 fw-medium text-center text-truncate px-2" style={{ width: 130, flexShrink: 0 }}>
                  {row.skill}
                </span>
                <div className="flex-fill d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                  <div
                    className="bg-secondary rounded-end-1"
                    style={{ width: `${(row.supply / maxSupply) * 80}%`, height: 16, minWidth: 3 }}
                  />
                  <span className="fs-12 fw-semibold">{row.supply}</span>
                  {row.supply === 0 && <span className="badge bg-danger-transparent fs-10">No supply</span>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </SectionCard>
  )
}

// ─── Recent hires ────────────────────────────────────────────────────────────

// Also used on the recruiter dashboard, where every hire is the viewer's own
// organization — so the employer name is dropped and "View All" points elsewhere.
export function RecentHires({
  hires,
  viewAllTo = '/admin/hires',
  showOrganization = true,
}: {
  hires: DashboardRecentHire[]
  viewAllTo?: string
  showOrganization?: boolean
}) {
  return (
    <SectionCard
      title="Recent Hires"
      subtitle="Latest candidates marked as hired"
      action={
        <Link to={viewAllTo} className="btn btn-sm btn-primary-light">
          View All
        </Link>
      }
    >
      {hires.length === 0 ? (
        <EmptyState icon="ti-trophy" text="No hires recorded yet." />
      ) : (
        <ul className="list-unstyled mb-0 d-flex flex-column gap-3">
          {hires.map((hire, i) => (
            <li key={hire.applicationId} className="d-flex align-items-center gap-3">
              <InitialsAvatar name={hire.candidate} index={i} />
              <div className="flex-fill" style={{ minWidth: 0 }}>
                <p className="fs-13 fw-semibold mb-0 text-truncate">{hire.candidate ?? 'Candidate'}</p>
                <p className="fs-12 text-muted mb-0 text-truncate">
                  {showOrganization ? `${hire.jobTitle} · ${hire.organization}` : hire.jobTitle}
                </p>
              </div>
              <span className="fs-11 text-muted text-nowrap">{formatRelativeTime(hire.hiredAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  )
}

// ─── Top employers ───────────────────────────────────────────────────────────

export function TopEmployers({ rows }: { rows: DashboardEmployerRow[] }) {
  return (
    <SectionCard
      title="Top Employers"
      subtitle="Ranked by hires"
      bodyClass="card-body p-0"
      action={
        <Link to="/admin/organizations" className="btn btn-sm btn-primary-light">
          View All
        </Link>
      }
    >
      {rows.length === 0 ? (
        <EmptyState icon="ti-building" text="No employers yet." />
      ) : (
        <div className="table-responsive">
          <table className="table text-nowrap mb-0">
            <thead>
              <tr>
                <th className="fs-12">Employer</th>
                <th className="fs-12 text-end">Jobs</th>
                <th className="fs-12 text-end">Applications</th>
                <th className="fs-12 text-end">Hires</th>
                <th className="fs-12" style={{ width: 130 }}>Conversion</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const conversion = percent(row.hires, row.applications)
                return (
                  <tr key={row.id}>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <InitialsAvatar name={row.name} index={i} />
                        <div>
                          <p className="fs-13 fw-semibold mb-0">{row.name}</p>
                          <StatusPill status={row.status} className="fs-10" />
                        </div>
                      </div>
                    </td>
                    <td className="text-end fs-13">{row.jobs.toLocaleString()}</td>
                    <td className="text-end fs-13">{row.applications.toLocaleString()}</td>
                    <td className="text-end fs-13 fw-semibold">{row.hires.toLocaleString()}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div className="progress progress-xs flex-fill">
                          <div className="progress-bar bg-success" style={{ width: `${Math.min(conversion, 100)}%` }} />
                        </div>
                        <span className="fs-12 text-muted" style={{ width: 40 }}>{conversion}%</span>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  )
}

// ─── Program performance ─────────────────────────────────────────────────────

export function ProgramPerformance({ rows }: { rows: DashboardProgramRow[] }) {
  return (
    <SectionCard
      title="Program Performance"
      subtitle="Students per program and phase, by furthest stage"
      bodyClass="card-body p-0"
      action={
        <Link to="/admin/lms-sources" className="btn btn-sm btn-primary-light">
          Programs
        </Link>
      }
    >
      {rows.length === 0 ? (
        <EmptyState icon="ti-school" text="No student profiles yet." />
      ) : (
        <div className="table-responsive">
          <table className="table text-nowrap mb-0">
            <thead>
              <tr>
                <th className="fs-12">Program</th>
                <th className="fs-12 text-end">Students</th>
                <th className="fs-12 text-end">Applied</th>
                <th className="fs-12 text-end">Shortlisted</th>
                <th className="fs-12 text-end">Hired</th>
                <th className="fs-12" style={{ width: 130 }}>Placed</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const placed = percent(row.hired, row.applied)
                return (
                  <tr key={`${row.program}|${row.phase ?? ''}`}>
                    <td>
                      <p className="fs-13 fw-semibold mb-0">{row.program}</p>
                      <span className={`badge fs-10 ${row.isPortal ? 'bg-info-transparent' : 'bg-primary-transparent'}`}>
                        {row.isPortal ? 'Direct sign-ups' : (row.phase ?? 'No phase')}
                      </span>
                    </td>
                    <td className="text-end fs-13">{row.students.toLocaleString()}</td>
                    <td className="text-end fs-13">{row.applied.toLocaleString()}</td>
                    <td className="text-end fs-13">{row.shortlisted.toLocaleString()}</td>
                    <td className="text-end fs-13 fw-semibold">{row.hired.toLocaleString()}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div className="progress progress-xs flex-fill">
                          <div className="progress-bar bg-primary" style={{ width: `${Math.min(placed, 100)}%` }} />
                        </div>
                        <span className="fs-12 text-muted" style={{ width: 40 }}>{placed}%</span>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  )
}
